import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import axios from 'axios';
import config from '../config';

function Approvals() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approver, setApprover] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [activeToken, setActiveToken] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPending();
    const user = localStorage.getItem('verichain_user') || '';
    setApprover(user);
  }, []);

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/recalls/pending`);
      setPending(response.data.pending || []);
    } catch (error) {
      console.error('Error fetching pending recalls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (token) => {
    if (!approver) { setError('Please enter your name or email first.'); return; }
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${config.API_URL}/batch/recall/approve`, {
        token,
        approver,
      });
      setSuccess('Recall approved and activated on the blockchain. All Buy Safe consumers have been notified.');
      setActiveToken(null);
      setActiveAction(null);
      fetchPending();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve recall.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (token) => {
    if (!approver) { setError('Please enter your name or email first.'); return; }
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${config.API_URL}/batch/recall/reject`, {
        token,
        approver,
        rejectReason,
      });
      setSuccess('Recall rejected. The batch remains active.');
      setActiveToken(null);
      setActiveAction(null);
      setRejectReason('');
      fetchPending();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject recall.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-ZA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="page">
      <Navbar />

      <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFFFFF' }}>
            Recall Approvals
          </h1>
          <p style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
            Review and action pending recall requests. Your approval activates the recall on the blockchain.
          </p>
        </div>

        {/* Approver Identity */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <label className="label">Your Name / Email (Approver)</label>
          <input
            className="input"
            value={approver}
            onChange={(e) => setApprover(e.target.value)}
            placeholder="Enter your name or email to authorize actions"
          />
          <p style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
            This will be permanently recorded on the blockchain as the authorizing manager.
          </p>
        </div>

        {/* Success / Error */}
        {success && (
          <div style={{
            backgroundColor: '#0D2A1A',
            border: '1px solid #22A860',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
          }}>
            <p style={{ color: '#22A860', fontSize: '13px' }}>✓ {success}</p>
          </div>
        )}
        {error && <p className="error" style={{ marginBottom: '16px' }}>{error}</p>}

        {/* Pending Recalls */}
        {loading ? (
          <p style={{ color: '#64748B' }}>Loading pending recalls...</p>
        ) : pending.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
            <p style={{ fontSize: '32px', marginBottom: '16px' }}>✓</p>
            <p style={{ color: '#22A860', fontSize: '16px', fontWeight: 'bold' }}>
              No Pending Approvals
            </p>
            <p style={{ color: '#64748B', fontSize: '13px', marginTop: '8px' }}>
              All recall requests have been actioned.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {pending.map((item, i) => (
              <div key={i} className="card">

                {/* Batch Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #1E3A6E',
                }}>
                  <div>
                    <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#F0B429' }}>
                      {item.batchId}
                    </p>
                    <p style={{ fontSize: '13px', color: '#FFFFFF', marginTop: '4px' }}>
                      {item.product}
                    </p>
                  </div>
                  <span style={{
                    backgroundColor: '#2D1F00',
                    border: '1px solid #F0B429',
                    color: '#F0B429',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}>
                    Pending Approval
                  </span>
                </div>

                {/* Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '16px',
                }}>
                  {[
                    ['Manufacturer', item.manufacturer],
                    ['Quantity', `${item.quantity} units`],
                    ['Initiated By', item.initiator],
                    ['Initiated At', formatDate(item.createdAt)],
                    ['Expires At', formatDate(item.expiresAt)],
                  ].map(([label, value]) => (
                    <div key={label} style={{
                      backgroundColor: '#0B1D35',
                      padding: '10px 12px',
                      borderRadius: '6px',
                    }}>
                      <p style={{ fontSize: '11px', color: '#64748B', marginBottom: '2px' }}>{label}</p>
                      <p style={{ fontSize: '13px', color: '#FFFFFF' }}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Recall Reason */}
                <div style={{
                  backgroundColor: '#0a1c35',
                  borderLeft: '3px solid #F0B429',
                  padding: '12px 16px',
                  marginBottom: '24px',
                  borderRadius: '0 6px 6px 0',
                }}>
                  <p style={{ fontSize: '11px', color: '#F0B429', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                    Recall Reason
                  </p>
                  <p style={{ fontSize: '13px', color: '#CBD5E1', lineHeight: '1.6' }}>
                    {item.reason}
                  </p>
                </div>

                {/* Action Buttons */}
                {activeToken === item.token ? (
                  <div>
                    {activeAction === 'reject' && (
                      <div style={{ marginBottom: '16px' }}>
                        <label className="label">Rejection Reason</label>
                        <textarea
                          className="input"
                          placeholder="Provide a reason for rejecting this recall..."
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={3}
                        />
                      </div>
                    )}

                    {activeAction === 'approve' && (
                      <div style={{
                        backgroundColor: '#2D1515',
                        border: '1px solid #C0392B',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                      }}>
                        <p style={{ color: '#E74C3C', fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
                          ⚠ Final Confirmation
                        </p>
                        <p style={{ color: '#CBD5E1', fontSize: '12px', lineHeight: '1.6' }}>
                          You are about to approve the recall of <strong style={{ color: '#FFFFFF' }}>{item.batchId}</strong>.
                          This action is irreversible and will be permanently recorded on the blockchain.
                          All Buy Safe consumers who scanned this product will be notified immediately.
                        </p>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        className="btn-primary"
                        onClick={() => activeAction === 'approve' ? handleApprove(item.token) : handleReject(item.token)}
                        disabled={processing}
                        style={{
                          backgroundColor: activeAction === 'approve' ? '#22A860' : '#C0392B',
                          background: activeAction === 'approve' ? '#22A860' : '#C0392B',
                        }}
                      >
                        {processing ? 'Processing...' : activeAction === 'approve' ? '✓ Confirm Approval' : '✕ Confirm Rejection'}
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => { setActiveToken(null); setActiveAction(null); setRejectReason(''); }}
                        disabled={processing}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className="btn-primary"
                      onClick={() => { setActiveToken(item.token); setActiveAction('approve'); setError(''); setSuccess(''); }}
                      style={{ background: '#22A860', backgroundColor: '#22A860' }}
                    >
                      ✓ Approve Recall
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => { setActiveToken(item.token); setActiveAction('reject'); setError(''); setSuccess(''); }}
                    >
                      ✕ Reject Recall
                    </button>
                  </div>
                )}

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

export default Approvals;